import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { UserX } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/Select';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { SEARCH_DEBOUNCE_MS } from './KontakterListPage';
import { formatDateTime, formatDateWithYear, formatNumber } from '../lib/format';
import {
  UNSUBSCRIBE_DETAIL_SOURCE_LABELS,
  UNSUBSCRIBE_REASON_LABELS,
} from '../lib/unsubscribeLabels';
import { ApiError } from '../types/api';
import type {
  SuppressionLookupResponse,
  SuppressionsOverviewResponse,
  UnsubscribeReason,
  UnsubscribeSource,
  UnsubscribesListResponse,
} from '../types/api';

const SOURCE_LABELS: Record<UnsubscribeSource, string> = {
  link_click: 'Unsubscribe link in a mail',
  header_one_click: 'Unsubscribe button in the mail app',
  manual: 'Deleted by an admin',
  import: 'Mailchimp import',
};

const PAGE_SIZE = 20;

/** Something@something.tld: worth a fingerprint lookup for the unsubscribes kept without the address. */
const WHOLE_ADDRESS = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** One plain sentence for what a lookup found. */
export function describeLookup(result: SuppressionLookupResponse): string {
  const { suppression, lastUnsubscribe, contactStatus } = result;
  if (suppression) {
    const since = formatDateWithYear(suppression.since);
    switch (suppression.reason) {
      case 'unsubscribed': {
        const how = lastUnsubscribe
          ? ` (${SOURCE_LABELS[lastUnsubscribe.source].toLowerCase()})`
          : '';
        const on = formatDateWithYear(lastUnsubscribe?.at ?? suppression.since);
        return `Unsubscribed on ${on}${how}. The address is never mailed again and never re-added.`;
      }
      case 'hard_bounce':
        return `Blocked since ${since}: mail to the address bounced (hard bounce).`;
      case 'complaint':
        return `Blocked since ${since}: a mail was marked as spam.`;
      case 'manual':
        return `Blocked by hand since ${since}.`;
    }
  }
  if (lastUnsubscribe && contactStatus === 'active') {
    return `Unsubscribed on ${formatDateWithYear(lastUnsubscribe.at)}, but signed up again since. An active contact now.`;
  }
  if (contactStatus === 'active') return 'Not blocked. An active contact.';
  if (contactStatus === 'suppressed') return 'A contact marked as suppressed: receives no mail.';
  return 'Not found. Not a contact, and never unsubscribed or blocked.';
}

function Stat({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <Card className="p-4 flex flex-col gap-1">
      <div className="text-xs text-muted">{label}</div>
      <div className="text-2xl font-semibold font-mono">{formatNumber(value)}</div>
      {note ? <div className="text-xs text-muted">{note}</div> : null}
    </Card>
  );
}

/** The reasons, most given first, each as a bar against the others. A click filters the list. */
function ReasonsCard({
  reasons,
  selected,
  onSelect,
}: {
  reasons: SuppressionsOverviewResponse['reasons'];
  selected: string | undefined;
  onSelect: (reason: UnsubscribeReason) => void;
}) {
  const given = Object.values(reasons.byReason).reduce((a, b) => a + b, 0);
  const ranked = (Object.keys(UNSUBSCRIBE_REASON_LABELS) as UnsubscribeReason[])
    .map((reason) => ({ reason, count: reasons.byReason[reason] }))
    .sort((a, b) => b.count - a.count);

  return (
    <Card className="p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-sm font-semibold m-0">Why people unsubscribe</h2>
        <span className="text-xs text-muted">
          {formatNumber(given)} of {formatNumber(reasons.total)} gave a reason
        </span>
      </div>
      {given === 0 ? (
        <p className="m-0 text-sm text-muted">
          No reasons yet. After unsubscribing from a mail sent here, people can pick a reason. It is
          optional.
        </p>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col gap-1">
          {ranked.map(({ reason, count }) => {
            const share = count / given;
            return (
              <li key={reason}>
                <button
                  type="button"
                  aria-pressed={selected === reason}
                  onClick={() => onSelect(reason)}
                  className="w-full grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 rounded-lg px-2 py-1.5 -mx-2 text-left text-sm hover:bg-bg aria-pressed:bg-bg"
                >
                  <span className={count === 0 ? 'text-muted' : ''}>
                    {UNSUBSCRIBE_REASON_LABELS[reason]}
                  </span>
                  <span className="font-mono text-[13px] text-muted">
                    {formatNumber(count)} · {Math.round(share * 100)}%
                  </span>
                  <span
                    className="col-span-2 h-1.5 overflow-hidden rounded-full bg-border-soft"
                    aria-hidden="true"
                  >
                    <span className="block h-full bg-accent" style={{ width: `${share * 100}%` }} />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/**
 * Audience → Unsubscribed. An unsubscribe deletes the contact at once; a
 * hash of the address blocks it from being mailed or re-added. This page
 * shows the counts, the reasons people gave, and each unsubscribe,
 * searchable on its address as on Contacts. Unsubscribes from before 26 Oct
 * 2026 have no address: when an admin types a whole one, the server also
 * hashes it and answers from the hash.
 */
export function AfmeldtePage() {
  const { isAdmin } = useAuth();
  const [q, setQ] = useState('');
  const debouncedQ = useDebouncedValue(q.trim(), SEARCH_DEBOUNCE_MS);
  const [reason, setReason] = useState<string | undefined>(undefined);
  // Keyset paging as on Contacts: Next pushes the server's `nextCursor`, Previous pops.
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];

  const overview = useQuery({
    queryKey: ['suppressions'],
    queryFn: () => apiFetch<SuppressionsOverviewResponse>('/suppressions'),
  });

  const list = useQuery({
    queryKey: ['suppressions', 'unsubscribes', debouncedQ, reason, cursor],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (debouncedQ) params.set('q', debouncedQ);
      if (reason) params.set('reason', reason);
      if (cursor) params.set('cursor', cursor);
      params.set('limit', String(PAGE_SIZE));
      return apiFetch<UnsubscribesListResponse>(`/suppressions/unsubscribes?${params.toString()}`, {
        signal,
      });
    },
  });

  const lookup = useQuery({
    queryKey: ['suppressions', 'lookup', debouncedQ],
    queryFn: ({ signal }) =>
      apiFetch<SuppressionLookupResponse>('/suppressions/lookup', {
        method: 'POST',
        body: { email: debouncedQ },
        signal,
      }),
    enabled: isAdmin && WHOLE_ADDRESS.test(debouncedQ),
    // The address is only sent for this answer; nothing to keep around.
    gcTime: 0,
  });

  function filterOn(next: string | undefined) {
    setReason(next);
    setCursors([]);
  }

  const lookupError = lookup.error
    ? lookup.error instanceof ApiError && lookup.error.body.message
      ? lookup.error.body.message
      : 'The lookup failed. Please try again.'
    : null;

  const data = overview.data;

  return (
    <>
      <PageHeader
        title="Unsubscribed"
        badge={<MockBadge />}
        description="When someone unsubscribes, they are deleted from Contacts at once and are never mailed again; an order or import can never add them back. Each unsubscribe is listed below with what was kept. Search on part of an address, as on Contacts."
      />

      {overview.isLoading ? <div className="text-muted">Loading…</div> : null}
      {overview.isError ? <Alert>The unsubscribe figures could not be loaded.</Alert> : null}

      {data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat
              label="Unsubscribed"
              value={data.counts.unsubscribed}
              {...(data.unsubscribesBySource.import > 0
                ? {
                    note: `${formatNumber(data.unsubscribesBySource.import)} of them from the Mailchimp import`,
                  }
                : {})}
            />
            <Stat label="Hard bounces" value={data.counts.hard_bounce} />
            <Stat label="Spam complaints" value={data.counts.complaint} />
            <Stat label="Blocked by hand" value={data.counts.manual} />
          </div>
          <ReasonsCard
            reasons={data.reasons}
            selected={reason}
            onSelect={(r) => filterOn(reason === r ? undefined : r)}
          />
        </>
      ) : null}

      <div className="flex flex-wrap gap-2.5 items-end">
        <div className="flex-grow flex flex-col gap-1.5 min-w-[240px]">
          <label htmlFor="unsubscribe-search" className="text-[13px] font-medium">
            Search by email
          </label>
          <Input
            id="unsubscribe-search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setCursors([]);
            }}
            placeholder="e.g. gmx.de or anna@"
            autoComplete="off"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="reason-filter" className="text-[13px] font-medium">
            Reason
          </label>
          <Select
            value={reason ?? 'all'}
            onValueChange={(v) => filterOn(v === 'all' ? undefined : v)}
          >
            <SelectTrigger id="reason-filter" className="min-w-[220px]">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {(Object.keys(UNSUBSCRIBE_REASON_LABELS) as UnsubscribeReason[]).map((r) => (
                <SelectItem key={r} value={r}>
                  {UNSUBSCRIBE_REASON_LABELS[r]}
                </SelectItem>
              ))}
              <SelectItem value="none">No reason given</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {lookup.data || lookupError ? (
        <div className="flex flex-col gap-2 -mt-1">
          <div role="status" aria-live="polite">
            {lookup.data ? (
              <p className="m-0 text-sm">
                <span className="text-muted">Fingerprint check: </span>
                {describeLookup(lookup.data)}
              </p>
            ) : null}
          </div>
          {lookupError ? <Alert>{lookupError}</Alert> : null}
        </div>
      ) : null}

      {list.isError ? <Alert>The unsubscribes could not be loaded. Please try again.</Alert> : null}

      {list.isLoading ? (
        <div className="text-muted">Loading unsubscribes…</div>
      ) : list.isError ? null : (
        <>
          <div className="border border-border rounded-xl overflow-x-auto">
            <table className="w-full border-collapse text-left text-sm">
              <caption className="sr-only">Unsubscribes</caption>
              <thead>
                <tr className="border-b border-border-soft text-muted text-xs">
                  <th scope="col" className="px-4 py-3 font-normal">
                    Email
                  </th>
                  <th scope="col" className="px-4 py-3 font-normal w-[170px]">
                    When
                  </th>
                  <th scope="col" className="px-4 py-3 font-normal">
                    Reason
                  </th>
                  <th scope="col" className="px-4 py-3 font-normal">
                    How
                  </th>
                  <th scope="col" className="px-4 py-3 font-normal">
                    Mail
                  </th>
                  <th scope="col" className="px-4 py-3 font-normal w-[90px]">
                    Language
                  </th>
                  <th scope="col" className="px-4 py-3 font-normal w-[80px]">
                    Country
                  </th>
                </tr>
              </thead>
              <tbody>
                {(list.data?.items.length ?? 0) === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-3">
                      <EmptyState
                        className="border-0 py-8"
                        icon={<UserX />}
                        title={
                          debouncedQ
                            ? 'No address matches'
                            : reason
                              ? 'None with this reason'
                              : 'No unsubscribes yet'
                        }
                        description={
                          debouncedQ
                            ? 'Unsubscribes from before 26 Oct 2026 were kept without the address, so a search cannot find them.'
                            : reason
                              ? 'Nobody has unsubscribed with this reason yet.'
                              : 'Unsubscribes from mails sent here show up in this list. Those from the Mailchimp import are only counted above.'
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  list.data?.items.map((u) => (
                    // `relative` + the link's `after:inset-0` makes the whole row clickable, as on Contacts.
                    <tr
                      key={u.id}
                      className="relative border-b border-border-soft last:border-b-0 hover:bg-bg"
                    >
                      <td className="px-4 py-3 font-medium break-all">
                        <Link
                          to={`/afmeldte/${u.id}`}
                          className={`no-underline after:absolute after:inset-0 after:content-[''] ${u.email ? 'text-text' : 'text-muted font-normal'}`}
                        >
                          {u.email ?? 'Not kept'}
                        </Link>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted">
                        {formatDateTime(u.occurredAt)}
                      </td>
                      <td className={u.reason ? 'px-4 py-3' : 'px-4 py-3 text-muted'}>
                        {u.reason ? UNSUBSCRIBE_REASON_LABELS[u.reason] : 'Not given'}
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {UNSUBSCRIBE_DETAIL_SOURCE_LABELS[u.source]}
                      </td>
                      <td className="px-4 py-3 text-muted">{u.campaign?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-muted">{u.language ?? '—'}</td>
                      <td className="px-4 py-3 text-muted">{u.country ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted text-[13px]">
              {list.data ? `${formatNumber(list.data.total)} in total` : ''}
            </span>
            <div className="ml-auto flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={cursors.length === 0}
                onClick={() => setCursors((s) => s.slice(0, -1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!list.data?.nextCursor}
                onClick={() => {
                  const next = list.data?.nextCursor;
                  if (next) setCursors((s) => [...s, next]);
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
